import {Results} from '@orama/orama';
import {
  ArticleStatutEvolutionEnum,
  CadencierTendances,
  HitItemCatalogue,
} from 'api-types/ffconnect';

/** Ordre d'affichage des pastilles de tendance (hors « Tout »). */
export const TENDANCES_ORDER: readonly ArticleStatutEvolutionEnum[] = [
  'en-hausse',
  'gagne',
  'stable',
  'en-baisse',
  'perdu',
  'a-gagner',
] as const;

export type TendancesCounts = Record<ArticleStatutEvolutionEnum, number> & {
  tout: number;
};

export const emptyTendancesCounts = (): TendancesCounts => ({
  tout: 0,
  'en-hausse': 0,
  gagne: 0,
  stable: 0,
  'en-baisse': 0,
  perdu: 0,
  'a-gagner': 0,
});

/**
 * Construit une Map code_article → statut à partir du payload get-cadencier-tendances.
 */
export function buildStatutByCode(
  articles: CadencierTendances['articles'] | undefined,
): Map<string, ArticleStatutEvolutionEnum> {
  const map = new Map<string, ArticleStatutEvolutionEnum>();
  if (!articles) return map;
  for (const article of articles) {
    map.set(article.code_article, article.statut);
  }
  return map;
}

/**
 * Compte les hits Orama par statut de tendance (intersection Orama × map statuts).
 * Les codes absents de la map sont classés en `a-gagner` (aligné serveur).
 */
export function compterTendancesSurHits(
  hits: Results<HitItemCatalogue>['hits'] | undefined,
  statutByCode: Map<string, ArticleStatutEvolutionEnum>,
): TendancesCounts {
  const counts = emptyTendancesCounts();
  if (!hits) return counts;
  counts.tout = hits.length;
  for (const hit of hits) {
    const statut = statutByCode.get(hit.document.code) ?? 'a-gagner';
    counts[statut] += 1;
  }
  return counts;
}

/**
 * Filtre les hits Orama selon les tendances sélectionnées.
 * Sélection vide = tout afficher.
 */
export function filtrerHitsParTendances(
  hits: Results<HitItemCatalogue>['hits'],
  selected: ArticleStatutEvolutionEnum[],
  statutByCode: Map<string, ArticleStatutEvolutionEnum>,
): Results<HitItemCatalogue>['hits'] {
  if (selected.length === 0) return hits;
  const selectedSet = new Set(selected);
  return hits.filter((hit) => {
    const statut = statutByCode.get(hit.document.code) ?? 'a-gagner';
    return selectedSet.has(statut);
  });
}

type FacetValues = Record<string, number>;

/** Incrémente un compteur de facette pour une valeur scalaire ou un tableau. */
function incrementFacet(
  values: FacetValues,
  raw: string | string[] | boolean | number | undefined | null,
): void {
  if (raw === undefined || raw === null) return;
  if (Array.isArray(raw)) {
    for (const v of raw) {
      if (v === '') continue;
      values[v] = (values[v] ?? 0) + 1;
    }
    return;
  }
  const key = String(raw);
  if (key === '') return;
  values[key] = (values[key] ?? 0) + 1;
}

/**
 * Recalcule les facettes utilisées par FilterBar à partir d'une liste de hits.
 * Nécessaire pour que Promo / Labels / Fromage / Surgelé tiennent compte du filtre tendance.
 */
export function rebuildFacetsFromHits(
  hits: Results<HitItemCatalogue>['hits'],
): NonNullable<Results<HitItemCatalogue>['facets']> {
  const nature: FacetValues = {};
  const labels: FacetValues = {};
  const famille: FacetValues = {};
  const typeLait: FacetValues = {};
  const traitement: FacetValues = {};
  const temperature: FacetValues = {};
  const produitMarche: FacetValues = {};

  for (const hit of hits) {
    const doc = hit.document;
    incrementFacet(nature, doc.nature_du_prix);
    incrementFacet(labels, doc.labels);
    incrementFacet(famille, doc.famille_fromage);
    incrementFacet(typeLait, doc.type_de_lait);
    incrementFacet(traitement, doc.traitement_thermique_du_lait);
    incrementFacet(temperature, doc.temperature_livraison);
    if (doc.produit_marche !== undefined) {
      incrementFacet(produitMarche, doc.produit_marche);
    }
  }

  const facet = (values: FacetValues) => ({
    count: Object.keys(values).length,
    values,
  });

  return {
    nature_du_prix: facet(nature),
    labels: facet(labels),
    famille_fromage: facet(famille),
    type_de_lait: facet(typeLait),
    traitement_thermique_du_lait: facet(traitement),
    temperature_livraison: facet(temperature),
    produit_marche: facet(produitMarche),
  };
}

/**
 * Construit un Results Orama synthétique (hits filtrés + facettes recalculées)
 * pour alimenter FilterBar et construireSectionsFiltrees.
 */
export function buildFilteredItemResults(
  base: Results<HitItemCatalogue>,
  filteredHits: Results<HitItemCatalogue>['hits'],
): Results<HitItemCatalogue> {
  return {
    ...base,
    hits: filteredHits,
    count: filteredHits.length,
    facets: rebuildFacetsFromHits(filteredHits),
  };
}
